/* GSTIN verification — pharmacy-ims `POST v1/gst/verify`.
 *
 * Onboarding a supplier requires a GSTIN that the government register actually
 * answers for, so this is a gate, not a convenience: Suppliers.jsx will not save
 * a new supplier until a call here comes back verified.
 *
 * The IMS backend holds the provider key and forwards the lookup, so the browser
 * never talks to the provider directly. A hit comes back as the provider's own
 * body ({ success, response_message, result: {...} }); a rejected GSTIN is a 400
 * in the IMS envelope ({ success: false, message, data: null }).
 */
import { httpSupplier } from "../HttpCommonALL";

/* 22AAAAA0000A1Z5 — state code, PAN, entity number, 'Z', checksum. Checked
   before spending a billable lookup on an obvious typo. */
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

export const normalizeGstin = (v) => String(v || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15);

export const gstinFormatError = (v) => {
  const g = normalizeGstin(v);
  if (!g) return "GSTIN is required.";
  if (g.length !== 15) return "A GSTIN is 15 characters.";
  if (!GSTIN_RE.test(g)) return "That is not a valid GSTIN format.";
  return null;
};

/* The register answers "NA" for fields it has no value for (trade_name,
   last_updated, …). Treat that as blank so "NA" never becomes a supplier name. */
const clean = (v) => {
  const s = String(v ?? "").trim();
  return s.toUpperCase() === "NA" ? "" : s;
};

/* The register's address block -> the supplier form's address fields. `district` is the
   closest thing it has to a city; `state_code` holds the state NAME, not a code. */
const fromAddress = (a = {}) => ({
  full: clean(a.full_address),
  pincode: clean(a.pincode),
  city: clean(a.district) || clean(a.city),
  state: clean(a.state_code),
});

/* Verify one GSTIN.
   -> { ok: true,  gstin, legalName, tradeName, status, ... }  registered
   -> { ok: false, message }                                    no such GSTIN
   throws                                                       call never landed
   The third case is deliberately distinct from the second: "this GSTIN is not
   registered" is an answer, "verification is down" is not, and the screen must
   not let the second read as the first. */
export async function verifyGstin(gstin, { signal } = {}) {
  const g = normalizeGstin(gstin);
  let json;
  try {
    const res = await httpSupplier.post("v1/gst/verify", { gstin: g }, { signal });
    json = res.data;
  } catch (err) {
    /* A 4xx carrying a message is the backend's answer about this GSTIN
       ("must be 15 characters", "not registered"). Anything else — no response,
       5xx, cancelled — means the lookup never landed, so rethrow. */
    const status = err?.response?.status;
    const body = err?.response?.data;
    if (status >= 400 && status < 500 && body?.message) {
      return { ok: false, message: body.message };
    }
    throw new Error(body?.message || `GST verification failed${status ? ` (HTTP ${status})` : ""}.`);
  }

  const r = json?.result;
  if (!json?.success || !r) {
    return {
      ok: false,
      message: json?.message || json?.response_message || "No record found for this GSTIN.",
    };
  }

  const address = fromAddress(r.primary_business_address);
  return {
    ok: true,
    gstin: r.gstin || g,
    /* Legal name is what is on the register; trade name is what the business
       calls itself and is usually the better supplier name. Either can be blank. */
    legalName: clean(r.legal_name),
    tradeName: clean(r.trade_name),
    /* "Active" | "Cancelled" | "Suspended" — a cancelled registration verifies
       fine and is still a reason not to onboard, so the screen shows it. */
    status: clean(r.current_registration_status),
    constitution: clean(r.business_constitution),
    taxpayerType: clean(r.tax_payer_type),
    registeredOn: clean(r.register_date),
    cancelledOn: clean(r.register_cancellation_date),
    address,
    raw: r,
  };
}

const gstVerifyService = { verifyGstin, gstinFormatError, normalizeGstin, GSTIN_RE };

export default gstVerifyService;
